import React from "react";
import { Link } from "react-router-dom";
import {
  Workflow, FileText, MessagesSquare, Boxes, Stethoscope,
  Lock, Activity, Database, ArrowRight, Layers, GitBranch,
} from "lucide-react";
import SEO, { breadcrumb, softwareApplicationSchema } from "../components/SEO";

const PRODUIT_SCHEMA = [
  softwareApplicationSchema,
  breadcrumb([{ name: "Accueil", url: "/" }, { name: "Produit", url: "/produit" }]),
];

export default function Produit() {
  return (
    <div className="bg-[var(--mc-navy-950)]" data-testid="page-produit">
      <SEO
        title="Plateforme e-santé modulaire et conforme ANS · MetaConnect"
        description="Coordination, télésanté, messagerie sécurisée, documents de santé : tous les modules MetaConnect dans une plateforme conforme aux référentiels nationaux."
        canonical="/produit"
        schema={PRODUIT_SCHEMA}
      />
      <section className="relative hex-grid pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl fade-up">
            <Eyebrow>Produit</Eyebrow>
            <h1 className="mt-4 font-display text-[#0F2B5B] text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Une plateforme modulaire pour <span className="text-[#0369A1]">orchestrer</span> les parcours de soins
            </h1>
            <p className="mt-6 text-[16px] text-[#475569] leading-relaxed">
              MetaConnect réunit dans une seule architecture les briques essentielles
              pour numériser, sécuriser et coordonner les parcours patient, en s'appuyant
              sur les services socles nationaux du numérique en santé.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="btn-primary px-6 py-3 rounded-full text-[14px] font-medium inline-flex items-center gap-2" data-testid="produit-cta-demo">
                Planifier une démo <ArrowRight size={16} />
              </Link>
              <Link to="/conformite" className="btn-ghost px-6 py-3 rounded-full text-[14px] font-medium" data-testid="produit-cta-conformite">
                Voir la conformité
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 lg:py-28 hex-soft">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <Workflow size={22} />, title: "Coordination & parcours", desc: "Orchestration end-to-end du parcours patient avec rôles et permissions granulaires." },
            { icon: <FileText size={22} />, title: "Documents de santé", desc: "Comptes rendus, ordonnances, bilans : partage sécurisé et alimentation DMP/MES." },
            { icon: <MessagesSquare size={22} />, title: "Messagerie sécurisée", desc: "Connexion native MSSanté pour les échanges interprofessionnels conformes." },
            { icon: <Database size={22} />, title: "Gestion des données patient", desc: "Identito-vigilance INSi, qualification d'identités et historique consolidé." },
            { icon: <Lock size={22} />, title: "Authentification forte", desc: "Pro Santé Connect / e-CPS pour les professionnels, OTP et SSO disponibles." },
            { icon: <Activity size={22} />, title: "Téléservices", desc: "Téléconsultation, téléexpertise, télésuivi : modules prêts à activer." },
            { icon: <Layers size={22} />, title: "API & SDK", desc: "Intégration aux logiciels métier via API REST et webhooks documentés." },
            { icon: <GitBranch size={22} />, title: "Workflows configurables", desc: "Adaptez les parcours à vos protocoles sans développement spécifique." },
            { icon: <Boxes size={22} />, title: "Hébergement HDS", desc: "Données hébergées en France, conformes au référentiel HDS." },
          ].map((m) => (
            <div key={m.title} className="mc-card p-7" data-testid={`feature-${m.title.toLowerCase().slice(0, 12)}`}>
              <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(45,123,255,0.15)] border border-[rgba(127,179,255,0.28)] text-[#0369A1]">
                {m.icon}
              </div>
              <h3 className="mt-5 font-display text-[#0F2B5B] text-[18px]">{m.title}</h3>
              <p className="mt-2.5 text-[13.5px] text-[#475569] leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <FinalBand />
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1]">
      <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
      {children}
    </div>
  );
}

function FinalBand() {
  return (
    <section className="bg-[var(--mc-navy-900)] py-20">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
        <h2 className="font-display text-[#0F2B5B] text-3xl lg:text-4xl">
          Prêt à voir MetaConnect en action ?
        </h2>
        <p className="mt-4 text-[#475569] max-w-2xl mx-auto">
          Une démo personnalisée pour explorer les modules adaptés à votre environnement.
        </p>
        <div className="mt-7">
          <Link to="/demo" className="btn-primary px-7 py-3.5 rounded-full text-[14px] font-medium inline-flex items-center gap-2">
            Planifier une démo <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
